import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Order } from '../types/myaccount';
import api from '../services/api';
import { TableSkeleton, ToolbarSkeleton } from './MyAccountLoadingStates';
import DataTable from './DataTable';
import { formatMoneyWithCurrencyCode } from '../utils/formatMoneyWithCurrencyCode';
import { getStatusBadgeClassName } from '../utils/statusBadges';

type SortField = 'order_number' | 'date_created' | 'status' | 'total';
type SortDirection = 'asc' | 'desc';

interface InvoicePaymentsTabProps {
    enabled?: boolean;
}

const InvoicePaymentsTab: React.FC<InvoicePaymentsTabProps> = ({ enabled = true }) => {
    const [paymentOrders, setPaymentOrders] = useState<Order[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState<SortField>('date_created');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const fetchPaymentOrders = async () => {
        try {
            setLoading(true);
            setError(null);
            const orders = await api.getOrders({ page: 1, per_page: 100, kind: 'invoice_payment' });
            setPaymentOrders(orders);
        } catch (err) {
            console.error('Error fetching invoice payment orders:', err);
            setError('Failed to load invoice payment orders. Please try again.');
            setPaymentOrders([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!enabled) {
            setLoading(false);
            setPaymentOrders([]);
            return;
        }

        fetchPaymentOrders();
    }, [enabled]);

    if (!enabled) {
        return (
            <div className="alert alert-info" role="status">
                Viewing invoice payment history is disabled for this page.
            </div>
        );
    }

    const formatDate = (dateString?: string): string => {
        if (!dateString) {
            return '-';
        }

        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) {
            return '-';
        }

        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const formatCurrency = (order: Order) => formatMoneyWithCurrencyCode(
        order.total,
        order.currency,
        order.currency_display_mode ?? order.show_currency_code,
        order.currency_symbol,
    );

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection(field === 'date_created' ? 'desc' : 'asc');
        }

        setCurrentPage(1);
    };

    const getSortIcon = (field: SortField) => {
        if (sortField !== field) {
            return <i className="fas fa-sort ms-1 text-muted"></i>;
        }

        return sortDirection === 'asc'
            ? <i className="fas fa-sort-up ms-1"></i>
            : <i className="fas fa-sort-down ms-1"></i>;
    };

    const filteredOrders = (paymentOrders || []).filter((order) => {
        if (!searchTerm) {
            return true;
        }

        const search = searchTerm.toLowerCase();
        const orderNumber = String(order.order_number || order.id).toLowerCase();
        const invoiceRefs = Array.isArray(order.paid_invoice_ids) ? order.paid_invoice_ids.join(' ').toLowerCase() : '';
        const status = String(order.status || '').toLowerCase();
        const createdDate = String(order.date_created || '').toLowerCase();
        const appliedAt = String(order.invoice_payment_applied_at || '').toLowerCase();
        const total = String(order.total || '').toLowerCase();

        return orderNumber.includes(search)
            || invoiceRefs.includes(search)
            || status.includes(search)
            || createdDate.includes(search)
            || appliedAt.includes(search)
            || total.includes(search);
    });

    if (loading && !paymentOrders) {
        return (
            <>
                <ToolbarSkeleton />
                <TableSkeleton columns={6} rows={6} />
            </>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger" role="alert">
                <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                    <span>{error}</span>
                    <button type="button" onClick={fetchPaymentOrders} className="btn btn-outline-danger btn-sm">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <DataTable
            rows={filteredOrders}
            rowKey={(order) => order.id}
            tableId="invoice_payments_table"
            responsiveMode="stacked-cards"
            tableClassName="table table-hover"
            toolbar={{
                searchValue: searchTerm,
                onSearchChange: (value) => {
                    setSearchTerm(value);
                    setCurrentPage(1);
                },
                searchPlaceholder: 'Search payment orders, invoice refs, dates, totals...',
                entriesPerPage,
                onEntriesPerPageChange: (value) => {
                    setEntriesPerPage(value);
                    setCurrentPage(1);
                },
                filteredCount: filteredOrders.length,
                totalCount: paymentOrders?.length || 0,
                countLabel: 'payment orders',
                onClearSearch: () => {
                    setSearchTerm('');
                    setCurrentPage(1);
                },
            }}
            sortable={{
                field: sortField,
                direction: sortDirection,
                clientSide: true,
                onSort: (field) => handleSort(field as SortField),
                getSortIcon: (field) => getSortIcon(field as SortField),
            }}
            pagination={{
                currentPage,
                itemsPerPage: entriesPerPage,
                totalItems: filteredOrders.length,
                itemsLabel: 'payment orders',
                onPageChange: setCurrentPage,
                mode: 'full',
            }}
            emptyState={(
                <div className="text-center py-5">
                    <i className="fas fa-credit-card fa-3x text-muted mb-3"></i>
                    <p className="text-muted mb-0">
                        {searchTerm ? 'No invoice payment orders found matching your search.' : 'No invoice payment orders found yet.'}
                    </p>
                </div>
            )}
            columns={[
                {
                    id: 'order_number',
                    sortable: true,
                    header: 'Payment #',
                    mobileLabel: 'Payment #',
                    mobileDisplay: 'title',
                    sortValue: (order) => order.order_number || String(order.id),
                    render: (order) => (
                        <Link to={`/view-order/${order.id}`} className="weka-account-table-link text-decoration-none fw-semibold">
                            #{order.order_number || order.id}
                        </Link>
                    ),
                },
                {
                    id: 'invoice_refs',
                    header: 'Invoices',
                    mobileLabel: 'Invoices',
                    mobileDisplay: 'field',
                    render: (order) => {
                        const invoiceRefs = Array.isArray(order.paid_invoice_ids) ? order.paid_invoice_ids : [];
                        if (invoiceRefs.length === 0) {
                            return <span className="text-muted">-</span>;
                        }

                        return (
                            <div className="d-flex flex-wrap gap-2">
                                {invoiceRefs.map((invoiceRef) => (
                                    <Link
                                        key={`${order.id}-${invoiceRef}`}
                                        to={`/invoices/${invoiceRef}`}
                                        className="badge rounded-pill weka-account-link weka-account-link--badge text-decoration-none"
                                    >
                                        #{invoiceRef}
                                    </Link>
                                ))}
                            </div>
                        );
                    },
                },
                {
                    id: 'date_created',
                    sortable: true,
                    header: 'Created Date',
                    mobileLabel: 'Created',
                    mobileDisplay: 'field',
                    sortValue: (order) => new Date(order.date_created || '').getTime() || 0,
                    render: (order) => formatDate(order.date_created),
                },
                {
                    id: 'status',
                    sortable: true,
                    header: 'Status',
                    mobileLabel: 'Status',
                    mobileDisplay: 'field',
                    sortValue: (order) => order.status || '',
                    render: (order) => (
                        <div>
                            <span className={getStatusBadgeClassName('orders', order.status)}>
                                {(order.status || 'unknown').replace(/-/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())}
                            </span>
                            {order.invoice_payment_applied_at && (
                                <div className="small text-muted mt-1">
                                    Applied {formatDate(order.invoice_payment_applied_at)}
                                </div>
                            )}
                        </div>
                    ),
                },
                {
                    id: 'total',
                    sortable: true,
                    header: 'Total',
                    mobileLabel: 'Total',
                    mobileDisplay: 'field',
                    headerClassName: 'text-end',
                    cellClassName: 'text-end',
                    sortValue: (order) => parseFloat(order.total || '0'),
                    render: (order) => formatCurrency(order),
                },
                {
                    id: 'actions',
                    header: 'Actions',
                    mobileDisplay: 'actions',
                    render: (order) => {
                        const payAction = Array.isArray(order.account_actions)
                            ? order.account_actions.find((action) => action && action.key === 'pay' && action.url)
                            : undefined;

                        return (
                            <div className="d-flex flex-wrap gap-2">
                                {payAction && (
                                    <a href={payAction.url} className="btn btn-sm btn-success">
                                        Pay
                                    </a>
                                )}
                                <Link to={`/view-order/${order.id}`} className="btn btn-sm btn-outline-secondary">
                                    View
                                </Link>
                            </div>
                        );
                    },
                },
            ]}
        />
    );
};

export default InvoicePaymentsTab;
